<script>
export default {
  name: "BaseList"
}
</script>
<script setup>
import { useInsideModel, usePagination } from "@/utils"
import { PullRefresh, List, Empty } from "vant"
import { computed, ref, shallowRef } from "vue"

const $props = defineProps({
  api: { type: Function },
  size: { type: Number, default: 10 },
  params: { type: [Object, Function] },
  listContainerClass: { type: String },

  useRefresh: { type: Boolean }, // 是否开启下拉刷新操作
  useFullPageRefresh: { type: Boolean, default: false }, // 数据量不足一页时，是否全屏都可触发下拉刷新
  pullProps: { type: Object },
  pullLoading: { type: Boolean, default: false },

  usePaging: { type: Boolean }, // 是否开启分页操作
  listProps: { type: Object },
  listLoading: { type: Boolean, default: false },
  listError: { type: Boolean, default: false }
})

const $emit = defineEmits([
  "update:pullLoading",
  "update:listLoading",
  "update:listError"
])

const insidePullLoad = useInsideModel({ $props, $emit, propKey: "pullLoading" })
const insideListLoad = useInsideModel({ $props, $emit, propKey: "listLoading" })
const insideListError = useInsideModel({ $props, $emit, propKey: "listError" })

const { total, page, records, getApi, next, loading } = usePagination({
  api: $props.api,
  pagination: { size: $props.size },
  params: $props.params
})

const data = shallowRef([])
const isFinish = ref(false)

function onListLoad() {
  if (isFinish.value) return
  if (loading.value) return

  /** 无数据加载第一页，有则加载第一页 */
  insideListLoad.value = true
  const api = total.value ? next : getApi
  api().then(() => {
    data.value = [...data.value, ...records.value]
    isFinish.value = total.value <= data.value.length
    insideListLoad.value = false
  })
}
function onPullLoad() {
  page.value = 1
  insidePullLoad.value = true
  getApi().then(() => {
    data.value = [...records.value]
    isFinish.value = total.value <= data.value.length
    insidePullLoad.value = false
  })
}

const $pull = computed(() => {
  if ($props.useFullPageRefresh) {
    return {
      pullingText: "下拉即可刷新...",
      loosingText: "释放即可刷新...",
      loadingText: "加载中...",
      successText: "加载成功",
      class: "full-page",
      ...$props.pullProps
    }
  }

  return {
    pullingText: "下拉即可刷新...",
    loosingText: "释放即可刷新...",
    loadingText: "加载中...",
    successText: "加载成功",
    ...$props.pullProps
  }
})

const $list = computed(() => {
  return {
    errorText: "加载失败，请重试",
    loadingText: "加载中...",
    finishedText: "没有数据了",
    emptyText: "暂无数据",
    offset: 40,
    ...$props.listProps
  }
})

const baseRef = ref()
const scrollTo = (value) => {
  baseRef.value.scrollTop = value
}
const getScrollTop = () => {
  return baseRef.value.scrollTop
}

defineExpose({
  data,
  loading,
  reload: onPullLoad,
  scrollTo,
  getScrollTop
})
</script>

<template>
  <div class="base-list" ref="baseRef">
    <div class="top-pane">
      <slot name="top"></slot>
    </div>

    <PullRefresh
      v-model="insidePullLoad"
      :disabled="!useRefresh"
      @refresh="onPullLoad"
      v-bind="$pull"
    >
      <List
        v-model:loading="insideListLoad"
        v-model:error="insideListError"
        :finished="isFinish"
        :disabled="!usePaging"
        v-bind="$list"
        @load="onListLoad"
      >
        <slot name="container" :data="data" :loading="loading">
          <div :class="listContainerClass">
            <template v-for="(item, i) in data" :key="i">
              <slot :item="item" :index="i" />
            </template>
          </div>
        </slot>
        <template #loading>
          <slot name="loading">{{ $list.loadingText }}</slot>
        </template>
        <template #finished>
          <Empty
            v-if="!data.length && !loading"
            :description="$list.emptyText"
          ></Empty>
          <slot name="finished" v-else>{{ $list.finishedText }}</slot>
        </template>
        <template #error>
          <slot name="error">{{ $list.errorText }}</slot>
        </template>
      </List>

      <template #normal>
        <slot name="pull-normal"></slot>
      </template>
      <template #pulling>
        <slot name="pull-pulling">{{ $pull.pullingText }}</slot>
      </template>
      <template #loosing="scope">
        <slot name="pull-loosing" v-bind="scope">{{ $pull.loosingText }}</slot>
      </template>
      <template #loading="scope">
        <slot name="pull-loading" v-bind="scope">{{ $pull.loadingText }}</slot>
      </template>
      <template #success>
        <slot name="pull-success">{{ $pull.successText }}</slot>
      </template>
    </PullRefresh>

    <div class="footer-pane">
      <slot name="footer"></slot>
    </div>
  </div>
</template>

<style lang="less" scoped>
.base-list {
  overflow-x: hidden;
  overflow-y: auto;
}
.full-page {
  height: 100%;
  flex-grow: 1;
  overflow-y: auto;
}
</style>
